import { createRouter, createWebHistory } from 'vue-router'
import Default from '@/layouts/Default.vue'
import HomePage from '@/layouts/HomePage.vue'
import InputForm from '@/components/InputForm.vue'
import ValidateCreative from '@/components/ValidateCreative.vue'
import GenerateCreative from '@/components/GenerateCreative.vue'
import EditorPage from '@/layouts/EditorPage.vue'
import CreativePlayground from '@/components/CreativePlayground.vue'
import ProfileSelection from '@/views/Authentication/ProfileSelection.vue'
import Authentication from '@/views/Authentication/Authentication.vue'
import LoginUser from '@/views/Authentication/LoginUser.vue'
import AuthCallback from '@/views/Authentication/AuthCallback.vue'
import PageNotFound from '@/views/Error/PageNotFound.vue'
import ConvertCreative from '@/components/ConvertCreative.vue'

import AppService from '@/services/appService'
import { useAppStore } from '@/stores/app'
import { useAppCookies } from '@/composables/useCookies'
import errorCodes from '@/constants/errorCodes'
import InvalidRequestDataException from '@/errors/InvalidRequestDataException'
import TokenExpiredException from '@/errors/TokenExpiredException'
import TokenInvalidException from '@/errors/TokenInvalidException'
import UnauthorizedError from '@/errors/UnauthorizedError'
import { embedMessageTypes } from '@/constants/embed'
import type { EmbedUser } from '@/types/embedUser'
import { authCallbackGuard, globalRouterGuard, loginGuard } from '@/router/authGuards'
import { authService } from '@/api/client'

const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
      {
        path: '/login',
        name: 'Login',
        component: LoginUser,
        meta: { requiresAuth: false },
        beforeEnter: loginGuard,
      },
      {
        path: '/callback',
        name: 'Callback',
        component: AuthCallback,
        meta: { requiresAuth: false },
      },
      {
        path: '/',
        name: 'Default',
        component: Default,
        children: [
          {
            path: '/auth',
            name: 'Authentication',
            component: Authentication,
            meta: { requiresAuth: true },
            children: [
              {
                path: '/profileSelection',
                name: 'ProfileSelection',
                component: ProfileSelection,
                meta: {
                  title: 'Profile Selection',
                  requiresAuth: true,
                },
              },
            ],
          },
          {
            path: 'app/:businessProfileId?',
            name: 'App',
            component: HomePage,
            meta: { requiresAuth: true },
            beforeEnter: async (to, from, next) => {
              const businessProfileId = Number(to.params.businessProfileId)
              if (isNaN(businessProfileId) || businessProfileId <= 0) {
                next({ name: 'NotFound' })
                return
              }
              if (to.matched[to.matched.length - 1].name === 'App') {
                next({ name: 'Mode', params: { businessProfileId } })
                return
              }
              const appStore = useAppStore()
              if (appStore.businessProfiles.length === 0) {
                const appService = new AppService()
                appStore.setBusinessProfiles(
                  await appService.getPortalBusinessProfiles(
                    appStore.user?.id,
                    appStore.portal?.id,
                  ),
                )
              }

              const hasAccess = appStore.businessProfiles.some((bp) => bp.id === businessProfileId)
              if (!hasAccess) {
                next({ name: 'NotFound' })
                return
              }

              appStore.setSelectedBusinessProfileId(Number(to.params.businessProfileId))
              next()
            },
            children: [
              {
                path: 'mode',
                name: 'Mode',
                component: InputForm,
                meta: { requiresAuth: true },
              },
              {
                path: 'validate',
                name: 'Validate',
                component: ValidateCreative,
                meta: { requiresAuth: true },
              },
              {
                path: 'generate',
                name: 'Generate',
                component: GenerateCreative,
                meta: { requiresAuth: true },
              },
              {
                path: 'editor',
                name: 'Editor',
                component: EditorPage,
                meta: { requiresAuth: true },
              },
              {
                path: 'download',
                name: 'Download',
                component: CreativePlayground,
                meta: { requiresAuth: true },
              },
              {
                path: 'convert',
                name: 'Convert',
                component: ConvertCreative,
                meta: { requiresAuth: true },
              },
            ],
          },
          {
            path: '/not-found',
            name: 'NotFound',
            component: PageNotFound,
          },
          {
            path: '/forbidden',
            name: 'Forbidden',
            component: () => import('@/views/Error/PageForbidden.vue'),
          },
        ],
      },
      {
        path: '/:pathMatch(.*)*',
        redirect: { name: 'NotFound' },
      },
    ],
  })

  router.beforeEach(async (to, from, next) => {
    const appStore = useAppStore()
    const appService = new AppService()
    const { getCookies, setCookies } = useAppCookies()

    // Embed flow — unchanged until embed is migrated to IdP
    if (to.query.token) {
      appService.removeUserTokens()

      if (to.query.userId) {
        try {
          const embedLoginResponse = (await appService.loginWithEmbedToken(
            to.query.token.toString(),
            to.query.userId.toString(),
            to.query.brandId?.toString(),
          )) as {
            accessToken: string
            userId: string
            brandId?: number
          }

          setCookies('accessToken', { accessToken: embedLoginResponse.accessToken })
          authService.setAccessToken(embedLoginResponse.accessToken)
          const clientUser = await appService.getUserById(embedLoginResponse.userId)
          setCookies('user', (clientUser as { data: unknown[] }).data[0])

          if (to.query.brandId) {
            embedLoginResponse.brandId = +to.query.brandId
          }
          appStore.setEmbedUser(embedLoginResponse as unknown as EmbedUser)
        } catch (e: unknown) {
          handleEmbedFlowError(e)
          appService.removeUserTokens()
        }
      }
    }

    if (to.query.token) {
      const user = getCookies('user')
      const accessToken = getCookies('accessToken')

      if (user && accessToken && appStore.user == null) {
        appStore.setUser(user as Parameters<typeof appStore.setUser>[0])
      }

      next()
      return
    }

    if (to.name === 'Login' || to.name === 'Callback') {
      next()
      return
    }

    await globalRouterGuard(to, from, next)
  })

  return router
}

const handleEmbedFlowError = (e: unknown) => {
  let errorName = 'UnknownError'
  let errorCode = 500
  const error = e as { message?: string }

  if (e instanceof InvalidRequestDataException) {
    errorName = InvalidRequestDataException.name
    errorCode = errorCodes.InvalidRequestDataException
  } else if (e instanceof TokenExpiredException) {
    errorName = TokenExpiredException.name
    errorCode = errorCodes.TokenExpiredException
  } else if (e instanceof TokenInvalidException) {
    errorName = TokenInvalidException.name
    errorCode = errorCodes.TokenInvalidException
  } else if (e instanceof UnauthorizedError) {
    errorName = UnauthorizedError.name
    errorCode = errorCodes.Unauthorized
  }

  window.top?.postMessage(
    {
      type: embedMessageTypes.EMBED_ERROR,
      name: errorName,
      code: errorCode,
      message: error?.message,
    },
    '*',
  )
}

export default createAppRouter
